import {
  Box,
  Button,
  ClickAwayListener,
  colors,
  InputAdornment,
  Popper,
  TextField,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  numberToString,
  stringToNumber,
  stripNonNumericInput,
} from '../../special/numberConverter';
import TypeCheckbox from './TypeCheckbox';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';

const TypeNumber = ({
  field,
  dataCollection,
  errors,
  updateValidationOnChange,
  startDataCollection,
  size,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const [value, setValue] = useState(dataCollection[field.key] || '');
  const [focused, setFocused] = useState(false);

  const { t } = useTranslation();

  function numberToStringHelper(value) {
    return numberToString(
      value,
      field.unformatted
        ? 0
        : dataCollection[`${field.key}DecimalPlaces`] || field.decimalPlaces,
      field.unformatted || field.removeThousandSeparator
        ? ''
        : dataCollection[`${field.key}ThousandSeperator`]
    );
  }

  function stringToNumberHelper(value) {
    return stringToNumber(
      value,
      field.unformatted
        ? 0
        : dataCollection[`${field.key}DecimalPlaces`] || field.decimalPlaces,
      field.unformatted || field.removeThousandSeparator
        ? ''
        : dataCollection[`${field.key}ThousandSeperator`]
    );
  }

  const [reminderAnchorEl, setreminderAnchorEl] = useState(null);
  function handleOpenNewCall(e) {
    setreminderAnchorEl(e.currentTarget);
  }

  useEffect(() => {
    if (!startDataCollection || !startDataCollection[field.key]) return;
    setValue(numberToStringHelper(startDataCollection[field.key] || ''));
  }, [startDataCollection]);

  useEffect(() => {
    if (focused) return;
    if (dataCollection[field.key] !== undefined)
      setValue(numberToStringHelper(dataCollection[field.key]));
  }, [dataCollection[field.key]]);

  function handleFocus() {
    setFocused(true);
    if (!dataCollection[field.key] || dataCollection[field.key] === 0)
      setValue('');
  }

  function handleTextChange(value) {
    const typed = stripNonNumericInput(value.target.value);
    if (stringToNumberHelper(typed) !== 0) {
      updateValidationOnChange([field.key], stringToNumberHelper(typed));
    }
    if (typed === '') {
      updateValidationOnChange([field.key], stringToNumberHelper(''));
    }
    setValue(typed);
  }

  function handleBlur(value) {
    setFocused(false);

    if (stringToNumberHelper(value.target.value) !== 0) {
      updateValidationOnChange(
        [field.key],
        stringToNumberHelper(value.target.value)
      );
      setValue(numberToStringHelper(stringToNumberHelper(value.target.value)));
    }

    if (value.target.value === '' || value.target.value.trim() == '') {
      updateValidationOnChange([field.key], null);
      setValue('');
      return;
    }

    if (stringToNumberHelper(value.target.value) === 0) {
      setValue(numberToStringHelper(dataCollection[field.key] || ''));
      return;
    }
  }

  return (
    <>
      <Box className="h-full flex w-full gap-2">
        <TextField
          label={field.label}
          value={value}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={handleTextChange}
          multiline={Boolean(field.multiline)}
          size={size}
          sx={{
            width: '100%',
          }}
          inputProps={{ inputMode: 'decimal' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">{field.unit}</InputAdornment>
            ),
          }}
        />
        {field.allowFormat && (
          <Button
            sx={{
              bgcolor: colors.bgInput,
              ':hover': { bgcolor: colors.selected },
            }}
            onClick={handleOpenNewCall}
          >
            format ICON
          </Button>
        )}
      </Box>

      <Popper open={Boolean(reminderAnchorEl)} anchorEl={reminderAnchorEl}>
        <ClickAwayListener onClickAway={() => setreminderAnchorEl(null)}>
          <Box className="flex gap-4 mt-2">
            <TypeNumber
              field={{
                label: t('Decimal Places'),
                key: `${field.key}DecimalPlaces`,
              }}
              dataCollection={dataCollection}
              updateValidationOnChange={updateValidationOnChange}
            />
            <TypeCheckbox
              field={{
                label: t('Thousand Seperator'),
                key: `${field.key}ThousandSeperator`,
              }}
              dataCollection={dataCollection}
              updateValidationOnChange={updateValidationOnChange}
            />
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default TypeNumber;
